import { useId, useState } from 'react';

const CELL_SIZE = 64;
const OFFSET = 30;
const FONT = 'Bitstream Vera Sans, DejaVu Sans, Helvetica, Arial, sans-serif';

const cellOrigin = (cell) => ({
  x: OFFSET + cell.column * CELL_SIZE,
  y: OFFSET + cell.row * CELL_SIZE,
});

function Stone({ x, y }) {
  return (
    <g data-stone="true">
      <rect x={x} y={y} width="64" height="64" fill="#3f3f3f" />
      <polygon points={`${x},${y} ${x + 64},${y} ${x + 48},${y + 16} ${x + 16},${y + 16}`} fill="#666666" />
      <polygon points={`${x},${y} ${x + 16},${y + 16} ${x + 16},${y + 48} ${x},${y + 64}`} fill="#545454" />
      <polygon points={`${x + 64},${y} ${x + 64},${y + 64} ${x + 48},${y + 48} ${x + 48},${y + 16}`} fill="#303030" />
      <polygon points={`${x},${y + 64} ${x + 64},${y + 64} ${x + 48},${y + 48} ${x + 16},${y + 48}`} fill="#282828" />
      <rect x={x + 16} y={y + 16} width="32" height="32" fill="#444444" />
      <rect x={x} y={y} width="64" height="64" fill="none" stroke="#1d1d1d" strokeWidth="1.2" />
    </g>
  );
}

// Every edge is visited once; stone-to-stone edges remain thin.
function boundaries(layout) {
  const lines = [];
  for (let row = 0; row < 8; row += 1) {
    for (let column = 0; column < 8; column += 1) {
      const label = layout[row][column];
      if (column < 7 && label !== layout[row][column + 1]) {
        lines.push([column + 1, row, column + 1, row + 1]);
      }
      if (row < 7 && label !== layout[row + 1][column]) {
        lines.push([column, row + 1, column + 1, row + 1]);
      }
    }
  }
  return lines;
}

/** Display only: values, geometry, and persistent candidates come from the authorized step. */
export default function NumberKingdomsBoard({
  geometry,
  values = [],
  givens = [],
  candidates = [],
  candidateDisplays = [],
  assignedCells = [],
  eliminatedCells = [],
  patternCells = [],
  sourceUnit = null,
  ariaLabel = 'Number Kingdoms puzzle',
}) {
  const titleId = useId();
  const descriptionId = useId();
  const [showCandidates, setShowCandidates] = useState(false);
  const [enlarged, setEnlarged] = useState(false);
  const layout = geometry?.layout_grid;
  const cells = geometry?.cells || [];

  if (!layout || cells.length !== 48) {
    return <p className="py-16 text-center text-sm leading-6 text-slate-500">Load a puzzle to see its kingdoms and stones.</p>;
  }

  const givenSet = new Set(givens);
  const assignedSet = new Set(assignedCells);
  const eliminatedSet = new Set(eliminatedCells);
  const patternSet = new Set(patternCells);
  const sourceSet = new Set(sourceUnit?.cells || []);
  const displays = new Map(candidateDisplays.map((entry) => [entry.cell, entry]));
  const kingdomLabels = new Map();
  cells.forEach((cell) => {
    if (!kingdomLabels.has(cell.kingdom)) kingdomLabels.set(cell.kingdom, cell.cell);
  });

  return (
    <div className="min-w-0 space-y-3">
      <div className="flex flex-wrap gap-x-5 gap-y-2 text-sm text-slate-700">
        <label className="inline-flex items-center gap-2">
          <input type="checkbox" checked={showCandidates} onChange={(event) => setShowCandidates(event.target.checked)} className="accent-slate-800 focus-visible:ring-2 focus-visible:ring-brand-cyan" />
          Show all candidates
        </label>
        <label className="inline-flex items-center gap-2">
          <input type="checkbox" checked={enlarged} onChange={(event) => setEnlarged(event.target.checked)} className="accent-slate-800 focus-visible:ring-2 focus-visible:ring-brand-cyan" />
          Enlarge board
        </label>
      </div>
      <p className="text-xs leading-5 text-slate-500">Enlarge the board for bigger pencil marks; scroll across when needed. Row and column numbers match the hint coordinates.</p>
      <div role="region" aria-label="Scrollable Number Kingdoms board" tabIndex="0" className="min-w-0 max-w-full overflow-x-auto rounded-sm focus-visible:outline focus-visible:outline-2 focus-visible:outline-brand-cyan">
        <svg viewBox="0 0 552 552" role="img" aria-labelledby={`${titleId} ${descriptionId}`} className="h-auto w-full" style={{ minWidth: enlarged ? 736 : showCandidates ? 552 : 0 }}>
          <title id={titleId}>{ariaLabel}</title>
          <desc id={descriptionId}>Eight rows and columns, six outlined kingdoms, and sixteen dark stones. Circles mark placements, solid boxes mark the deduction pattern, dotted boxes mark its source unit, and dashed boxes mark eliminations. Slashed candidates were removed in this step. Full coordinates, values, and current candidates are available below in Board details.</desc>
          <rect width="552" height="552" fill="white" />
          {Array.from({ length: 8 }, (_, index) => (
            <g key={`coordinate-${index}`} fill="#555555" fontFamily={FONT} fontSize="13" textAnchor="middle">
              <text x={OFFSET + index * CELL_SIZE + 32} y="17">{index + 1}</text>
              <text x="13" y={OFFSET + index * CELL_SIZE + 37}>{index + 1}</text>
            </g>
          ))}
          {cells.map((cell) => {
            const { x, y } = cellOrigin(cell);
            const patterned = patternSet.has(cell.cell);
            const eliminated = eliminatedSet.has(cell.cell);
            const assigned = assignedSet.has(cell.cell);
            const source = sourceSet.has(cell.cell);
            return <rect key={`fill-${cell.cell}`} x={x} y={y} width="64" height="64" fill={patterned ? '#e4e4e4' : eliminated || assigned ? '#ededed' : source ? '#f2f2f2' : '#ffffff'} data-cell-index={cell.cell} />;
          })}
          {layout.flatMap((row, rowIndex) => [...row].map((label, column) => label === '#' ? <Stone key={`stone-${rowIndex}-${column}`} x={OFFSET + column * CELL_SIZE} y={OFFSET + rowIndex * CELL_SIZE} /> : null))}
          {Array.from({ length: 9 }, (_, index) => (
            <g key={`grid-${index}`} stroke="#777777" strokeWidth="1">
              <line x1={OFFSET + index * CELL_SIZE} y1={OFFSET} x2={OFFSET + index * CELL_SIZE} y2={OFFSET + 512} />
              <line x1={OFFSET} y1={OFFSET + index * CELL_SIZE} x2={OFFSET + 512} y2={OFFSET + index * CELL_SIZE} />
            </g>
          ))}
          {boundaries(layout).map(([x1, y1, x2, y2], index) => (
            <line key={`boundary-${index}`} x1={OFFSET + x1 * CELL_SIZE} y1={OFFSET + y1 * CELL_SIZE} x2={OFFSET + x2 * CELL_SIZE} y2={OFFSET + y2 * CELL_SIZE} stroke="#111111" strokeWidth="6" strokeLinecap="square" data-kingdom-boundary="true" />
          ))}
          <rect x={OFFSET} y={OFFSET} width="512" height="512" fill="none" stroke="#111111" strokeWidth="6" />
          {cells.map((cell) => {
            const { x, y } = cellOrigin(cell);
            const index = cell.cell;
            const value = values[index] || 0;
            const display = displays.get(index);
            const removed = new Set([...(display?.eliminatedValues || []), ...(display?.removedValues || [])]);
            const circled = new Set(display?.circledValues || []);
            const retained = new Set(display?.retainedValues || []);
            const relevant = patternSet.has(index) || eliminatedSet.has(index) || sourceSet.has(index);
            const marks = new Set([...(showCandidates ? candidates[index] || [] : relevant ? display?.values || [] : []), ...circled, ...retained, ...removed]);
            return (
              <g key={`content-${index}`}>
                {kingdomLabels.get(cell.kingdom) === index ? <text x={x + 9} y={y + 13} fontFamily={FONT} fontSize="10" fontWeight="700" fill="#555555" data-kingdom-label={cell.kingdom}>{cell.kingdom}</text> : null}
                {sourceSet.has(index) ? <rect x={x + 5} y={y + 5} width="54" height="54" fill="none" stroke="#666666" strokeWidth="1.7" strokeDasharray="2 3" data-source-cell={index} /> : null}
                {patternSet.has(index) ? <rect x={x + 7} y={y + 7} width="50" height="50" fill="none" stroke="#202020" strokeWidth="2" data-pattern-cell={index} /> : null}
                {eliminatedSet.has(index) ? <rect x={x + 5} y={y + 5} width="54" height="54" fill="none" stroke="#202020" strokeWidth="2" strokeDasharray="7 4" data-eliminated-cell={index} /> : null}
                {assignedSet.has(index) ? <circle cx={x + 32} cy={y + 32} r="25" fill="none" stroke="#151515" strokeWidth="2.5" data-assigned-cell={index} /> : null}
                {value ? (
                  <text x={x + 32} y={y + 46.4} textAnchor="middle" fontFamily={FONT} fontSize="40" fontWeight={assignedSet.has(index) ? '700' : '400'} fill={givenSet.has(index) || assignedSet.has(index) ? '#111111' : '#606060'} data-value-cell={index} data-given={givenSet.has(index)}>{value}</text>
                ) : [...marks].sort((left, right) => left - right).map((digit) => {
                  const cx = x + 32 + (((digit - 1) % 4) - 1.5) * 13;
                  const cy = y + 32 + (Math.floor((digit - 1) / 4) - 0.5) * 22;
                  return (
                    <g key={`candidate-${index}-${digit}`}>
                      {circled.has(digit) ? <ellipse cx={cx} cy={cy} rx="7" ry="9" fill="none" stroke="#202020" strokeWidth="1.2" /> : null}
                      <text x={cx} y={cy + 4.8} textAnchor="middle" fontFamily={FONT} fontSize="14" fontWeight={removed.has(digit) || circled.has(digit) || retained.has(digit) ? '700' : '400'} fill="#202020" data-candidate-cell={index} data-candidate-value={digit}>{digit}</text>
                      {removed.has(digit) ? <line x1={cx - 5} y1={cy + 7} x2={cx + 5} y2={cy - 7} stroke="#202020" strokeWidth="1.6" data-removed-candidate={`${index}-${digit}`} /> : null}
                    </g>
                  );
                })}
              </g>
            );
          })}
        </svg>
      </div>
      <p className="text-xs leading-5 text-slate-600">Letters A–F identify kingdoms. Circle: new placement. Solid box: pattern. Dotted box: source unit. Dashed box and slashed digit: removed candidate. Small digits show possibilities; an unmarked blank is still unsolved.</p>
      <details className="border-t border-slate-200 pt-3 text-sm text-slate-700">
        <summary className="cursor-pointer font-semibold focus-visible:outline focus-visible:outline-2 focus-visible:outline-brand-cyan">Board details: coordinates, values, and candidates</summary>
        <p className="mt-3 text-xs leading-5">Stones: {layout.flatMap((row, rowIndex) => [...row].flatMap((label, column) => label === '#' ? [`r${rowIndex + 1}c${column + 1}`] : [])).join(', ')}.</p>
        <div className="mt-3 max-h-72 overflow-y-auto">
          <table className="w-full text-left text-xs leading-6">
            <caption className="sr-only">Current Number Kingdoms board. Stones are blocked and have no candidates.</caption>
            <thead><tr><th scope="col">Cell</th><th scope="col">Kingdom</th><th scope="col">Value / candidates</th></tr></thead>
            <tbody>{cells.map((cell) => (
              <tr key={`detail-${cell.cell}`} className="border-t border-slate-100">
                <th scope="row" className="font-medium">{cell.label}</th>
                <td>{cell.kingdom}</td>
                <td>{values[cell.cell] ? `${values[cell.cell]}${givenSet.has(cell.cell) ? ' (given)' : assignedSet.has(cell.cell) ? ' (placed this step)' : ''}` : `Blank; candidates: ${(candidates[cell.cell] || []).join(', ')}`}</td>
              </tr>
            ))}</tbody>
          </table>
        </div>
      </details>
    </div>
  );
}
